import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { ConfigService } from './config.service';
import { SettingsService } from './settings.service';
import { StorageService } from './storage.service';
import { TranslateService } from '@ngx-translate/core';

export type HashtagValidationError =
  | 'empty'
  | 'tooLong'
  | 'invalidCharacters'
  | 'duplicate'
  | 'tooMany';

@Injectable({
  providedIn: 'root',
})
export class HashtagService {
  readonly maxTagLength = 100;
  readonly maxUserTags = 100;
  readonly tagPattern = /^[a-z0-9_-]+$/;

  private readonly storageKey = `observation_tags_${environment.appFlavor}`;
  private unresolvedPathsReported = false;

  constructor(
    private storage: StorageService,
    private config: ConfigService,
    private settings: SettingsService,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  normalizeTag(tag: string): string {
    return (tag || '').trim().replace(/^#+/, '').toLowerCase();
  }

  validateTag(
    tag: string,
    existingTags: string[] = [],
    userTagCount = existingTags.length
  ): HashtagValidationError | null {
    const normalized = this.normalizeTag(tag);
    if (!normalized) {
      return 'empty';
    }
    if (normalized.length > this.maxTagLength) {
      return 'tooLong';
    }
    if (!this.tagPattern.test(normalized)) {
      return 'invalidCharacters';
    }
    if (
      existingTags.some(
        (existing) => this.normalizeTag(existing) === normalized
      )
    ) {
      return 'duplicate';
    }
    if (userTagCount >= this.maxUserTags) {
      return 'tooMany';
    }
    return null;
  }

  async getPreconfiguredTags(): Promise<string[]> {
    await this.config.getConfig();
    const setup =
      this.config.settings?.citobs_application?.replacing_start_ui_setup || {};
    return this.parseTags(setup['data-observation_tags']);
  }

  async getVisiblePreconfiguredTags(): Promise<string[]> {
    const tags = await this.getPreconfiguredTags();
    return tags.filter((tag) => !tag.startsWith('_'));
  }

  async getUserTags(): Promise<string[]> {
    const stored = await this.storage.get<string[] | string>(this.storageKey);
    const values =
      typeof stored === 'string' ? this.parseStoredTags(stored) : stored;
    return this.uniqueTags(Array.isArray(values) ? values : []).slice(
      0,
      this.maxUserTags
    );
  }

  async saveUserTags(tags: string[]): Promise<void> {
    await this.storage.set(
      this.storageKey,
      this.uniqueTags(tags).slice(0, this.maxUserTags)
    );
  }

  async clearUserTags(): Promise<void> {
    await this.storage.delete(this.storageKey);
  }

  async getInterpolatedTags(): Promise<string[]> {
    await this.config.getConfig();
    const setup =
      this.config.settings?.citobs_application?.replacing_start_ui_setup || {};
    const mappings: Record<string, string> =
      setup['toprocess_data-observation_tags_values'] || {};
    const submissionRoles =
      this.config.settings?.citobs_application?.submission_role_apikeys || {};
    const savedSettings = await this.settings.getSettings();
    const selectedRole =
      savedSettings?.role ||
      (await this.storage.get<string>(
        `selected_role_${environment.appFlavor}`
      )) ||
      'submitter';
    const roleEntry = Object.entries(submissionRoles).find(
      ([, role]) =>
        (role as { role_technical_code: string })?.role_technical_code ===
        selectedRole
    );
    const role = roleEntry?.[1];
    const tags: string[] = [];
    const unresolved: string[] = [];

    for (const [tagPrefix, path] of Object.entries(mappings)) {
      const value = this.resolveInterpolation(path, role);
      if (value === null) {
        unresolved.push(path);
        continue;
      }
      const normalizedPrefix = tagPrefix.replace(/^#+/, '');
      const tag = this.normalizeTag(`_${normalizedPrefix}_${value}`);
      if (tag) {
        tags.push(tag);
      }
    }

    if (unresolved.length > 0 && !this.unresolvedPathsReported) {
      this.unresolvedPathsReported = true;
      await this.reportUnresolvedPaths(unresolved);
    }
    return this.uniqueTags(tags);
  }

  async getAllTagsForSubmission(): Promise<string[]> {
    const preconfigured = await this.getPreconfiguredTags();
    const interpolated = await this.getInterpolatedTags();
    const userTags = await this.getUserTags();
    return this.uniqueTags([...preconfigured, ...interpolated, ...userTags]);
  }

  async formatTagsForDescription(): Promise<string> {
    return (await this.getAllTagsForSubmission())
      .map((tag) => `#${tag}`)
      .join(' ');
  }

  async buildDescription(): Promise<string> {
    return this.formatTagsForDescription();
  }

  private parseTags(value: unknown): string[] {
    if (typeof value !== 'string') {
      return [];
    }
    return this.uniqueTags(value.split(','));
  }

  private parseStoredTags(value: string): string[] {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : value.split(',');
    } catch {
      return value.split(',');
    }
  }

  private uniqueTags(tags: string[]): string[] {
    return [
      ...new Set(tags.map((tag) => this.normalizeTag(tag)).filter(Boolean)),
    ];
  }

  private resolveInterpolation(path: string, role: any): string | null {
    if (!role || !path.startsWith('submission_role.')) {
      return null;
    }
    const property = path.substring('submission_role.'.length).trim();
    const value = role[property];
    return value === undefined || value === null || String(value).trim() === ''
      ? null
      : String(value).trim().toLowerCase();
  }

  private async reportUnresolvedPaths(paths: string[]): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('hashtags.configErrorHeader'),
      message: this.translate.instant('hashtags.configErrorMessage', {
        paths: paths.join(', '),
      }),
      buttons: [this.translate.instant('generic.OK')],
    });
    await alert.present();
  }
}
